<template>
  <div class="packing-entry min-h-screen flex flex-col bg-gray-50" style="direction: rtl;">
    <div v-if="!token" class="flex-1 flex items-center justify-center px-6 py-10">
      <div class="w-full max-w-sm">
        <div class="flex justify-center mb-6">
          <img src="/img/icons/exuni-logo.png" class="h-16 object-contain" alt="اکسونی">
        </div>
        <div class="bg-white rounded-2xl border p-5">
          <h1 class="text-base font-bold text-gray-800 text-center mb-4">ورود به ثبت پکینگ</h1>

          <div class="mb-3">
            <label class="block text-xs text-gray-500 mb-1">نام کاربری</label>
            <input
                v-model="loginUsername"
                type="text"
                autocomplete="username"
                class="w-full h-11 rounded-lg border px-3 text-sm outline-none focus:border-[#39506D]"
                @keyup.enter="focusLoginPassword"
            >
          </div>
          <div class="mb-1">
            <label class="block text-xs text-gray-500 mb-1">رمز عبور</label>
            <input
                ref="loginPasswordInput"
                v-model="loginPassword"
                type="password"
                autocomplete="current-password"
                class="w-full h-11 rounded-lg border px-3 text-sm outline-none focus:border-[#39506D]"
                @keyup.enter="submitLogin"
            >
          </div>

          <div v-if="needTwoFactor" class="mt-3">
            <label class="block text-xs text-gray-500 mb-1">کد تایید دو مرحله‌ای</label>
            <input
                v-model="twoFactorCode"
                type="text"
                inputmode="numeric"
                class="w-full h-11 rounded-lg border px-3 text-sm outline-none focus:border-[#39506D]"
                @keyup.enter="submitLogin"
            >
          </div>

          <div v-if="loginError" class="mt-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-2 text-center">
            {{ loginError }}
          </div>

          <button
              type="button"
              class="w-full h-11 rounded-xl bg-[#39506D] font-bold mt-4 disabled:opacity-40 flex items-center justify-center gap-2"
              :disabled="loggingIn"
              @click="submitLogin"
          >
            <i v-if="loggingIn" class="fas fa-spinner fa-spin text-white"></i>
            <span class="text-white">ورود</span>
          </button>
        </div>
      </div>
    </div>

    <template v-else>
    <header class="bg-[#39506D] text-white px-4 pt-4 pb-3 shadow-md">
      <div class="max-w-md mx-auto">
        <div class="flex items-center justify-between">
          <h1 class="text-lg font-bold">ثبت پکینگ</h1>
          <span class="text-xs bg-white/15 rounded-full px-3 py-1">
            {{ items.length }} قلم / {{ totalQty }} عدد
          </span>
        </div>

        <div class="mt-3 bg-white/10 rounded-xl p-1 flex gap-1">
          <button
              type="button"
              class="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
              :class="packingType === 'china' ? 'bg-white' : ''"
              @click="switchType('china')"
          >
            <span :class="packingType === 'china' ? 'text-[#39506D]' : 'text-white/80'">پکینگ خارجی</span>
          </button>
          <button
              type="button"
              class="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
              :class="packingType === 'domestic' ? 'bg-white' : ''"
              @click="switchType('domestic')"
          >
            <span :class="packingType === 'domestic' ? 'text-[#39506D]' : 'text-white/80'">پکینگ ایرانی</span>
          </button>
        </div>
      </div>
    </header>

    <main class="flex-1 max-w-md w-full mx-auto px-4 pt-4 pb-32">
      <div v-if="installBannerVisible" class="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
        <i class="fas fa-mobile-alt text-blue-500 text-xl"></i>
        <div class="flex-1 text-xs text-blue-800 leading-5">
          <template v-if="showInstallButton">این صفحه را به صفحه اصلی گوشی اضافه کنید تا مثل یک اپ جدا باز شود.</template>
          <template v-else-if="isIos">برای نصب: دکمه اشتراک‌گذاری Safari را بزنید و «Add to Home Screen» را انتخاب کنید.</template>
        </div>
        <button v-if="showInstallButton" type="button" class="text-xs font-medium bg-blue-600 rounded-lg px-3 py-1.5 shrink-0" @click="installApp">
          <span class="text-white">نصب</span>
        </button>
      </div>

      <template v-if="!isPreviewStage">
        <div v-if="!justSubmitted" class="bg-white rounded-2xl border p-3 flex items-center gap-2">
          <input
              ref="codeInput"
              v-model="manualCode"
              type="text"
              inputmode="text"
              placeholder="بارکد را اسکن کنید یا کد را وارد کنید"
              class="flex-1 min-w-0 text-base outline-none py-2 px-2 rounded-lg bg-gray-50"
              @keyup.enter="submitManualCode"
          >
          <button
              type="button"
              class="w-12 h-12 shrink-0 rounded-xl bg-[#39506D] flex items-center justify-center"
              @click="openScanner"
          >
            <i class="fas fa-camera text-lg text-white"></i>
          </button>
        </div>
        <div v-else class="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2 text-sm text-green-800">
          <i class="fas fa-check-circle text-green-600"></i>
          <span>این لیست قبلاً ثبت شده است. برای اسکن لیست جدید، ابتدا آن را پاک کنید.</span>
        </div>

        <div v-if="!items.length" class="mt-10 flex flex-col items-center text-center text-gray-400 gap-3">
          <i class="fas fa-box-open text-4xl"></i>
          <p class="text-sm leading-6">
            لیست خالی است.<br>
            بارکد محصول را اسکن کنید یا در بالا وارد کنید.
          </p>
        </div>

        <div v-else class="mt-4">
          <div class="flex items-center justify-end mb-2">
            <button type="button" class="text-xs text-red-500 flex items-center gap-1" @click="confirmClearAll">
              <i class="fas fa-trash"></i>
              <span>پاک‌سازی لیست</span>
            </button>
          </div>

          <div class="flex flex-col gap-2">
            <div
                v-for="(item, idx) in items"
                :key="item.code"
                class="bg-white rounded-xl border p-3 flex items-center justify-between gap-3"
                :class="{ 'opacity-60 pointer-events-none': justSubmitted }"
                @click="openItemEditor(idx)"
            >
              <div class="min-w-0">
                <div class="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                  <span class="truncate">{{ item.code }}</span>
                  <i v-if="!isItemComplete(item)" class="fas fa-exclamation-triangle text-amber-500 text-xs" title="اطلاعات ناقص"></i>
                </div>
                <div v-if="itemSummary(item)" class="text-xs text-gray-400 truncate mt-0.5">{{ itemSummary(item) }}</div>
              </div>

              <div class="flex items-center gap-2 shrink-0" @click.stop>
                <span class="text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg px-2 py-1.5">تعداد: {{ item.qty }}</span>
                <button type="button" class="w-9 h-9 flex items-center justify-center" @click="removeItem(idx)">
                  <i class="fas fa-trash text-sm text-red-500"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="submitStep === 'error'" class="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">
          {{ submitError }}
        </div>
      </template>

      <div v-else class="mt-2">
        <div class="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800 mb-3 leading-6">
          روی هر ستون بزنید و نوع آن را مشخص کنید (مثل ستون کد محصول یا تعداد)، سپس «تولید گزارش نهایی» را بزنید.
        </div>

        <div v-if="submitStep === 'error'" class="mb-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">
          {{ submitError }}
        </div>

        <div class="overflow-x-auto bg-white rounded-xl border">
          <table class="min-w-full text-xs">
            <thead>
              <tr>
                <th
                    v-for="h in headers"
                    :key="'h-' + h"
                    class="px-2 py-2 text-center cursor-pointer border-b whitespace-nowrap"
                    :class="typeForHeader(h) ? [headerBg(typeForHeader(h)), headerText(typeForHeader(h))] : 'text-gray-400'"
                    @click="showHeaderPopup(h)"
                >
                  {{ typeForHeader(h) ? headerTypesForType[typeForHeader(h)] : headerLabel(h) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows.slice(0, 10)" :key="i" class="border-b">
                <td
                    v-for="h in headers"
                    :key="h"
                    class="px-2 py-2 text-center whitespace-nowrap"
                    :class="typeForHeader(h) ? [headerBg(typeForHeader(h)), headerText(typeForHeader(h))] : 'text-gray-600'"
                    @click="showHeaderPopup(h)"
                >
                  {{ formatCell(row[h]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="rows.length > 10" class="text-xs text-gray-400 text-center mt-1">و {{ rows.length - 10 }} ردیف دیگر...</div>

        <button
            type="button"
            class="w-full h-12 rounded-xl bg-[#39506D] font-bold mt-4 disabled:opacity-40 flex items-center justify-center gap-2"
            :disabled="!canCreateReport || submitStep === 'extracting'"
            @click="createExtractedReport"
        >
          <i v-if="submitStep === 'extracting'" class="fas fa-spinner fa-spin text-white"></i>
          <span class="text-white">تولید گزارش نهایی</span>
        </button>
        <button type="button" class="w-full h-10 rounded-xl mt-2" @click="cancelPreview">
          <span class="text-gray-500 text-sm">انصراف و بازگشت</span>
        </button>
      </div>
    </main>

    <div v-if="!justSubmitted && !isPreviewStage" class="fixed bottom-0 inset-x-0 bg-white border-t px-4 pt-3" style="padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 12px);">
      <div class="max-w-md mx-auto flex items-center gap-3">
        <button
            type="button"
            class="flex-1 h-12 rounded-xl bg-[#39506D] font-bold text-base disabled:opacity-40 flex items-center justify-center gap-2"
            :disabled="!canSubmit"
            @click="submitList"
        >
          <i v-if="submitStep === 'uploading'" class="fas fa-spinner fa-spin text-white"></i>
          <span v-if="submitStep === 'error'" class="text-white">تلاش مجدد</span>
          <span v-else class="text-white">ثبت لیست پکینگ</span>
        </button>
      </div>
    </div>

    <div v-if="popupVisible" class="fixed inset-0 z-50 bg-black/40 flex items-end" @click.self="popupVisible = false">
      <div class="w-full max-w-md mx-auto bg-white rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto">
        <h3 class="font-bold text-base text-gray-800 mb-3">این ستون را به عنوان مشخص کنید:</h3>
        <div class="flex flex-col gap-2">
          <button
              v-for="(label, type) in headerTypesForType"
              :key="type"
              type="button"
              class="text-right rounded-lg px-4 py-3"
              :class="headerBg(type)"
              @click="setHeaderType(type)"
          >
            <span :class="headerText(type)">{{ label }}</span>
          </button>
          <button type="button" class="text-center rounded-lg px-4 py-3 mt-1 bg-gray-50" @click="popupVisible = false">
            <span class="text-gray-500">انصراف</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="scannerOpen" class="fixed inset-0 z-50 bg-black">
      <BarcodeScanner
          :active="scannerOpen"
          @detected="onScanDetected"
          @permission-denied="onCameraIssue"
          @unsupported="onCameraIssue"
      />

      <button type="button" class="absolute top-3 right-3 w-11 h-11 rounded-full bg-black/50 flex items-center justify-center" @click="closeScanner">
        <i class="fas fa-times text-lg text-white"></i>
      </button>

      <transition name="fade">
        <div v-if="scanFlash" class="absolute top-16 inset-x-4 bg-green-600/95 text-white rounded-xl px-4 py-3 text-center text-sm shadow-lg">
          <i class="fas fa-check-circle ml-1"></i>
          کد «{{ scanFlash.code }}» افزوده شد (تعداد: {{ scanFlash.qty }})
        </div>
      </transition>

      <div class="absolute bottom-0 inset-x-0 p-4" style="padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 16px);">
        <input
            ref="scannerCodeInput"
            v-model="scannerManualCode"
            type="text"
            placeholder="یا کد را دستی وارد کنید"
            class="w-full h-12 rounded-xl px-4 text-base outline-none bg-white/95"
            @keyup.enter="submitScannerManualCode"
        >
      </div>
    </div>

    <div v-if="editingItem" class="fixed inset-0 z-50 bg-black/40 flex items-end" @click.self="closeItemEditor">
      <div class="w-full max-w-md mx-auto bg-white rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-1">
          <h3 class="font-bold text-base text-gray-800">ویرایش اطلاعات کالا</h3>
          <button type="button" class="w-9 h-9 flex items-center justify-center" @click="closeItemEditor">
            <i class="fas fa-times text-gray-500"></i>
          </button>
        </div>
        <div class="text-xs text-gray-400 mb-4">بارکد: {{ editingItem.code }}</div>

        <div class="flex flex-col gap-3">
          <div>
            <label class="block text-xs text-gray-500 mb-1">تعداد<span class="text-red-500">*</span></label>
            <input
                v-model.number="editingItem.qty"
                type="text"
                inputmode="numeric"
                class="w-full h-11 rounded-lg border px-3 text-sm outline-none focus:border-[#39506D]"
            >
          </div>
          <div v-for="def in fieldDefs" :key="def.key">
            <div class="flex items-center justify-between mb-1">
              <label class="block text-xs text-gray-500">
                {{ def.label }}<span v-if="def.required" class="text-red-500">*</span>
              </label>
              <button v-if="!def.required" type="button" class="w-6 h-6 flex items-center justify-center" @click="removeFieldDef(def.key)">
                <i class="fas fa-trash text-red-400 text-xs"></i>
              </button>
            </div>
            <input
                v-if="isPriceField(def.key)"
                :value="formatThousands(editingItem.fields[def.key])"
                type="text"
                inputmode="decimal"
                class="w-full h-11 rounded-lg border px-3 text-sm outline-none focus:border-[#39506D]"
                @input="onPriceFieldInput(def.key, $event)"
            >
            <input
                v-else
                v-model="editingItem.fields[def.key]"
                type="text"
                class="w-full h-11 rounded-lg border px-3 text-sm outline-none focus:border-[#39506D]"
            >
          </div>
        </div>

        <div class="mt-4">
          <button v-if="!showAddField" type="button" class="text-sm flex items-center gap-1" @click="showAddField = true">
            <i class="fas fa-plus text-blue-600"></i>
            <span class="text-blue-600">افزودن فیلد جدید</span>
          </button>
          <div v-else class="flex items-center gap-2">
            <input
                ref="newFieldInput"
                v-model="newFieldLabel"
                type="text"
                placeholder="نام فیلد جدید"
                class="flex-1 h-11 rounded-lg border px-3 text-sm outline-none"
                @keyup.enter="addFieldDef"
            >
            <button type="button" class="h-11 px-4 rounded-lg bg-blue-600 shrink-0" @click="addFieldDef">
              <span class="text-white text-sm">افزودن</span>
            </button>
          </div>
        </div>

        <button type="button" class="w-full h-12 rounded-xl bg-[#39506D] font-bold mt-5" @click="closeItemEditor">
          <span class="text-white">ذخیره و بستن</span>
        </button>
      </div>
    </div>

    <div v-if="submitStep === 'done' && reportResult" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div class="max-w-md w-full bg-white rounded-2xl overflow-hidden">
        <div class="bg-gradient-to-l from-green-500 to-emerald-600 p-6 text-white text-center">
          <i class="fas fa-check-circle text-4xl mb-2"></i>
          <h2 class="text-lg font-bold">پکینگ با موفقیت ثبت شد</h2>
        </div>
        <div class="p-6 grid grid-cols-2 gap-3 text-center">
          <div class="bg-green-50 border border-green-200 rounded-lg p-3">
            <div class="text-xs text-green-700 mb-1">شناسه بسته</div>
            <div class="text-lg font-bold text-green-900">{{ reportResult.package_id }}</div>
          </div>
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="text-xs text-blue-700 mb-1">تعداد سفارش ایجاد شده</div>
            <div class="text-lg font-bold text-blue-900">{{ reportResult.rows_created }}</div>
          </div>
        </div>
        <div v-if="reportResult.notifications && reportResult.notifications.length" class="px-6 pb-2">
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800 leading-6">
            <p v-for="(n, i) in reportResult.notifications" :key="i">{{ n }}</p>
          </div>
        </div>
        <div class="p-6 pt-2 flex flex-col gap-2">
          <button type="button" class="w-full h-12 rounded-xl bg-[#39506D] font-bold" @click="goDetail(reportResult.package_id)">
            <span class="text-white">تکمیل اولیه</span>
          </button>
          <button type="button" class="w-full h-12 rounded-xl bg-gray-100 font-bold" @click="goList">
            <span class="text-gray-700">مشاهده گزارش کامل پکینگ‌ها</span>
          </button>
          <button type="button" class="w-full h-10 rounded-xl mt-1" @click="dismissSuccess">
            <span class="text-gray-400 text-sm">بستن</span>
          </button>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import BarcodeScanner from './BarcodeScanner.vue';

const NUMERIC_FIELD_KEYS = new Set(['price', 'consumerPrice']);

// Same header-type vocabulary as the two original manual-upload components
// (ManagerEntrancePackageUpload.vue / ManagerDomesticEntrancePackageUpload.vue) —
// the domestic backend understands "r"/"pr"/"rr" (consumer/base price in toman/rial)
// which china's endpoint has no concept of at all.
const HEADER_TYPES_CHINA = {
  tq: 'تعداد کل', s: 'SKU', n: 'نام اولیه', p: 'قیمت اولیه',
  g: 'کد محصول', b: 'چیدمان کارتون', qpb: 'تعداد در هر کارتون', bq: 'تعداد کارتون',
};
const HEADER_TYPES_DOMESTIC = {
  n: 'نام اولیه', p: 'قیمت پایه (تومان)', r: 'قیمت مصرف‌کننده (تومان)',
  pr: 'قیمت پایه (ریال)', rr: 'قیمت مصرف‌کننده (ریال)', tq: 'تعداد کل', g: 'کد محصول', s: 'sku',
};
const HEADER_COLOR_MAP = {
  g: { bg: 'bg-orange-50', text: 'text-orange-700' },
  n: { bg: 'bg-green-50', text: 'text-green-700' },
  p: { bg: 'bg-blue-50', text: 'text-blue-700' },
  r: { bg: 'bg-cyan-50', text: 'text-cyan-700' },
  pr: { bg: 'bg-teal-50', text: 'text-teal-700' },
  rr: { bg: 'bg-sky-50', text: 'text-sky-700' },
  tq: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
  s: { bg: 'bg-purple-50', text: 'text-purple-700' },
  b: { bg: 'bg-pink-50', text: 'text-pink-700' },
  qpb: { bg: 'bg-indigo-50', text: 'text-indigo-700' },
  bq: { bg: 'bg-rose-50', text: 'text-rose-700' },
};

function isValidNumber(value) {
  const s = String(value == null ? '' : value).trim();
  if (!s) return false;
  const n = Number(s);
  return !isNaN(n) && isFinite(n);
}

function extractBackendErrorDetail(err) {
  try {
    const data = err && err.response && err.response.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data === 'object') {
      const detail = data.detail || data.message || data.error;
      if (typeof detail === 'string' && detail.trim()) return detail.trim();
    }
  } catch (e) {
    // noop — fall through to no-detail
  }
  return '';
}

export default {
  name: 'PackingEntryView',
  components: { BarcodeScanner },
  props: {
    packingType: {
      type: String,
      default: 'china',
    },
  },
  data() {
    return {
      items: [],
      fieldDefs: this.defaultFieldDefs(),
      editingItemIndex: null,
      newFieldLabel: '',
      showAddField: false,
      manualCode: '',
      scannerOpen: false,
      scannerManualCode: '',
      scanFlash: null,
      submitStep: 'idle', // idle | uploading | preview | extracting | done | error
      submitError: '',
      uploadedFileId: null,
      rows: [],
      headers: [],
      selectedHeaders: {},
      popupVisible: false,
      clickedHeader: null,
      reportResult: null,
      justSubmitted: false,
      deferredInstallEvent: null,
      showInstallButton: false,
      loginUsername: '',
      loginPassword: '',
      loginError: '',
      loggingIn: false,
      needTwoFactor: false,
      twoFactorCode: '',
    };
  },
  computed: {
    totalQty() {
      return this.items.reduce((sum, i) => sum + (Number(i.qty) || 0), 0);
    },
    canSubmit() {
      return !this.justSubmitted && this.items.length > 0 && this.submitStep !== 'uploading';
    },
    headerTypesForType() {
      return this.packingType === 'domestic' ? HEADER_TYPES_DOMESTIC : HEADER_TYPES_CHINA;
    },
    canCreateReport() {
      return !!this.uploadedFileId && Object.keys(this.selectedHeaders).length > 0;
    },
    isPreviewStage() {
      return this.submitStep === 'preview' || (this.submitStep === 'error' && !!this.uploadedFileId);
    },
    draftKey() {
      return `packingEntryDraft_${this.packingType}`;
    },
    isIos() {
      return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
    },
    isStandaloneDisplay() {
      return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    },
    installBannerVisible() {
      return !this.isStandaloneDisplay && (this.showInstallButton || this.isIos);
    },
    editingItem() {
      return this.editingItemIndex !== null ? this.items[this.editingItemIndex] : null;
    },
  },
  watch: {
    packingType() {
      this.resetForTypeChange();
    },
    items: {
      handler() {
        this.schedulePersistDraft();
      },
      deep: true,
    },
    fieldDefs: {
      handler() {
        this.schedulePersistDraft();
      },
      deep: true,
    },
  },
  mounted() {
    this.restoreDraft();
    this.$nextTick(() => this.focusCodeInput());
    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    this.swapToPackingManifest();
  },
  beforeDestroy() {
    clearTimeout(this._draftTimer);
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    this.restoreOriginalManifest();
  },
  methods: {
    switchType(type) {
      if (type === this.packingType) return;
      this.$router.replace({ name: 'PackingEntry', params: { packingType: type } }).catch(() => {});
    },
    resetForTypeChange() {
      this.items = [];
      this.fieldDefs = this.defaultFieldDefs();
      this.editingItemIndex = null;
      this.submitStep = 'idle';
      this.submitError = '';
      this.reportResult = null;
      this.uploadedFileId = null;
      this.rows = [];
      this.headers = [];
      this.selectedHeaders = {};
      this.justSubmitted = false;
      this.restoreDraft();
    },

    focusCodeInput() {
      if (this.$refs.codeInput) {
        this.$refs.codeInput.focus();
      }
    },

    defaultFieldDefs() {
      const defs = [
        { key: 'name', label: 'نام اولیه', required: true },
        { key: 'price', label: 'قیمت اولیه', required: true },
      ];
      // قیمت مصرف‌کننده (r) only exists in the domestic backend's header vocabulary.
      if (this.packingType === 'domestic') {
        defs.push({ key: 'consumerPrice', label: 'قیمت مصرف‌کننده', required: true });
      }
      return defs;
    },
    emptyFieldsObject() {
      const fields = {};
      this.fieldDefs.forEach((def) => {
        fields[def.key] = '';
      });
      return fields;
    },
    ensureItemFieldsShape(item) {
      if (!item.fields) this.$set(item, 'fields', {});
      this.fieldDefs.forEach((def) => {
        if (!(def.key in item.fields)) this.$set(item.fields, def.key, '');
      });
    },

    openItemEditor(index) {
      this.ensureItemFieldsShape(this.items[index]);
      this.editingItemIndex = index;
      this.showAddField = false;
      this.newFieldLabel = '';
    },
    closeItemEditor() {
      this.editingItemIndex = null;
      this.showAddField = false;
      this.newFieldLabel = '';
    },
    addFieldDef() {
      const label = this.newFieldLabel.trim();
      if (!label) return;

      const key = `custom_${Date.now()}`;
      this.fieldDefs.push({ key, label, required: false });
      this.items.forEach((it) => this.ensureItemFieldsShape(it));
      this.newFieldLabel = '';
      this.showAddField = false;
    },
    removeFieldDef(key) {
      const def = this.fieldDefs.find((d) => d.key === key);
      if (!def || def.required) return;
      if (!window.confirm(`فیلد «${def.label}» از همه ردیف‌ها حذف شود؟`)) return;

      this.fieldDefs = this.fieldDefs.filter((d) => d.key !== key);
      this.items.forEach((it) => {
        if (it.fields && key in it.fields) this.$delete(it.fields, key);
      });
    },
    itemSummary(item) {
      if (!item.fields) return '';
      return this.fieldDefs
        .map((def) => {
          const value = String(item.fields[def.key] || '').trim();
          if (!value) return '';
          const display = this.isPriceField(def.key) ? this.formatThousands(value) : value;
          return `${def.label}: ${display}`;
        })
        .filter(Boolean)
        .join(' • ');
    },
    isPriceField(key) {
      return NUMERIC_FIELD_KEYS.has(key);
    },
    formatThousands(value) {
      // Digits plus a single decimal point (قیمت can be entered with ممیز).
      const cleaned = String(value == null ? '' : value).replace(/[^\d.]/g, '');
      if (!cleaned) return '';
      const dotIndex = cleaned.indexOf('.');
      if (dotIndex === -1) {
        return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
      const intPart = cleaned.slice(0, dotIndex).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const fracPart = cleaned.slice(dotIndex + 1);
      return `${intPart}.${fracPart}`;
    },
    onPriceFieldInput(key, event) {
      let cleaned = event.target.value.replace(/[^\d.]/g, '');
      const dotIndex = cleaned.indexOf('.');
      if (dotIndex !== -1) {
        // only the first "." counts as the decimal point; drop any further ones
        cleaned = cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, '');
      }
      this.$set(this.editingItem.fields, key, cleaned);
      const formatted = this.formatThousands(cleaned);
      event.target.value = formatted;
      const pos = formatted.length;
      this.$nextTick(() => {
        if (event.target === document.activeElement) {
          event.target.setSelectionRange(pos, pos);
        }
      });
    },
    isItemComplete(item) {
      const qtyStr = String(item.qty == null ? '' : item.qty).trim();
      if (!qtyStr || !isValidNumber(qtyStr) || Number(qtyStr) <= 0) return false;
      if (!item.fields) return this.fieldDefs.every((def) => !def.required);
      return this.fieldDefs.every((def) => {
        if (!def.required) return true;
        const value = String(item.fields[def.key] || '').trim();
        if (!value) return false;
        if (NUMERIC_FIELD_KEYS.has(def.key)) return isValidNumber(value);
        return true;
      });
    },

    addOrIncrementItem(rawCode) {
      const code = String(rawCode || '').trim();
      if (!code) return;

      const existing = this.items.find((i) => i.code === code);
      if (existing) {
        existing.qty = (Number(existing.qty) || 0) + 1;
        return existing.qty;
      }

      this.items.push({ code, qty: 1, fields: this.emptyFieldsObject() });
      return 1;
    },

    removeItem(index) {
      this.items.splice(index, 1);
    },
    confirmClearAll() {
      if (!window.confirm('همه ردیف‌های لیست پاک شوند؟ این کار قابل بازگشت نیست.')) return;

      this.items = [];
      this.clearDraft();
      this.justSubmitted = false;
      this.submitStep = 'idle';
      this.submitError = '';
      this.reportResult = null;
      this.uploadedFileId = null;
      this.rows = [];
      this.headers = [];
      this.selectedHeaders = {};
    },

    submitManualCode() {
      const code = this.manualCode.trim();
      if (!code) return;
      const qty = this.addOrIncrementItem(code);
      this.giveScanFeedback(code, qty);
      this.manualCode = '';
      this.$nextTick(() => this.focusCodeInput());
    },
    submitScannerManualCode() {
      const code = this.scannerManualCode.trim();
      if (!code) return;
      const qty = this.addOrIncrementItem(code);
      this.showScanFlash(code, qty);
      this.scannerManualCode = '';
      this.$nextTick(() => {
        if (this.$refs.scannerCodeInput) this.$refs.scannerCodeInput.focus();
      });
    },

    openScanner() {
      this.scannerOpen = true;
    },
    closeScanner() {
      this.scannerOpen = false;
      this.$nextTick(() => this.focusCodeInput());
    },
    onScanDetected(code) {
      const qty = this.addOrIncrementItem(code);
      this.showScanFlash(code, qty);
      this.giveScanFeedback(code, qty);
    },
    onCameraIssue() {
      // BarcodeScanner already shows its own denied/unsupported message;
      // the manual input at the bottom of the overlay remains fully usable.
    },
    giveScanFeedback(code, qty) {
      if (window.navigator && typeof window.navigator.vibrate === 'function') {
        window.navigator.vibrate(80);
      }
    },
    showScanFlash(code, qty) {
      this.scanFlash = { code, qty };
      clearTimeout(this._scanFlashTimer);
      this._scanFlashTimer = setTimeout(() => {
        this.scanFlash = null;
      }, 1200);
    },

    schedulePersistDraft() {
      clearTimeout(this._draftTimer);
      this._draftTimer = setTimeout(() => this.persistDraft(), 300);
    },
    persistDraft() {
      try {
        if (this.items.length) {
          localStorage.setItem(this.draftKey, JSON.stringify({
            items: this.items,
            fieldDefs: this.fieldDefs,
            justSubmitted: this.justSubmitted,
          }));
        } else {
          localStorage.removeItem(this.draftKey);
        }
      } catch (e) {
        // storage unavailable (e.g. private mode) — degrade silently, draft just won't persist
      }
    },
    restoreDraft() {
      try {
        const raw = localStorage.getItem(this.draftKey);
        if (!raw) return;
        const draft = JSON.parse(raw);
        if (draft && Array.isArray(draft.items) && draft.items.length) {
          if (Array.isArray(draft.fieldDefs) && draft.fieldDefs.length) {
            this.fieldDefs = draft.fieldDefs;
          }
          this.items = draft.items;
          this.items.forEach((it) => this.ensureItemFieldsShape(it));
          this.justSubmitted = !!draft.justSubmitted;
        }
      } catch (e) {
        // corrupt/unavailable draft, start from empty list
      }
    },
    clearDraft() {
      try {
        localStorage.removeItem(this.draftKey);
      } catch (e) {
        // noop
      }
    },

    validateItems() {
      for (let idx = 0; idx < this.items.length; idx++) {
        const item = this.items[idx];
        this.ensureItemFieldsShape(item);

        const qtyStr = String(item.qty == null ? '' : item.qty).trim();
        if (!qtyStr || !isValidNumber(qtyStr) || Number(qtyStr) <= 0) {
          return { valid: false, itemIndex: idx, message: `تعداد برای کد «${item.code}» را به‌درستی وارد کنید.` };
        }

        for (const def of this.fieldDefs) {
          if (!def.required) continue;
          const value = String(item.fields[def.key] || '').trim();

          if (!value) {
            return { valid: false, itemIndex: idx, message: `برای کد «${item.code}»، فیلد «${def.label}» را وارد کنید.` };
          }
          if (NUMERIC_FIELD_KEYS.has(def.key) && !isValidNumber(value)) {
            return { valid: false, itemIndex: idx, message: `مقدار «${def.label}» برای کد «${item.code}» باید یک عدد معتبر باشد.` };
          }
        }
      }
      return { valid: true };
    },

    buildWorkbookFile() {
      // Column layout: code, qty, ...fieldDefs (in the same order as fieldDefs).
      const headerRow = new Array(2 + this.fieldDefs.length).fill('');
      const rows = this.items.map((i) => {
        const fields = i.fields || {};
        const fieldValues = this.fieldDefs.map((def) => fields[def.key] || '');
        return [String(i.code), Number(i.qty), ...fieldValues];
      });
      const ws = XLSX.utils.aoa_to_sheet([headerRow, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      return new File([out], 'packing-scan.xlsx', {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    },

    submitList() {
      if (!this.canSubmit) return;

      const validation = this.validateItems();
      if (!validation.valid) {
        this.openItemEditor(validation.itemIndex);
        this.onSubmitError(validation.message);
        return;
      }

      this.submitStep = 'uploading';
      this.submitError = '';
      this.uploadedFileId = null;
      this.rows = [];
      this.headers = [];
      this.selectedHeaders = {};

      let file;
      try {
        file = this.buildWorkbookFile();
      } catch (e) {
        this.onSubmitError('ساخت فایل لیست با خطا مواجه شد.');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('format_type', 'indexed_dict');
      formData.append('start_col', 1);

      this.request({
        url: this.endpoint('file_handler/uploadPacking'),
        method: 'post',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        success: (data) => this.onUploadSuccess(data),
        error: (err) => {
          const detail = extractBackendErrorDetail(err);
          const base = 'آپلود لیست با خطا مواجه شد (مثلاً قطعی اینترنت).';
          this.onSubmitError(`${detail ? base + ' جزئیات: ' + detail : base} لیست شما حفظ شده، دوباره تلاش کنید.`);
        },
      });
    },

    // Same "indexed_dict" preview contract the original manual-upload flow uses
    // (ManagerDomesticEntrancePackageUpload.vue's uploadFile success handler):
    // headers is the raw sorted union of column keys, unfiltered — column key "0"
    // is the backend's reserved product-image slot and just renders as a thumbnail
    // in the preview, same as it always has; nothing here guesses a mapping, the
    // user taps each column and picks its type themselves, same path as normal mode.
    onUploadSuccess(data) {
      this.uploadedFileId = (data && data.id) || null;
      this.rows = (data && Array.isArray(data.rows)) ? data.rows : [];
      this.selectedHeaders = {};

      const allKeys = new Set();
      this.rows.forEach((r) => Object.keys(r || {}).forEach((k) => allKeys.add(k)));
      this.headers = Array.from(allKeys).sort((a, b) => {
        const an = parseInt(a, 10);
        const bn = parseInt(b, 10);
        if (!isNaN(an) && !isNaN(bn)) return an - bn;
        if (!isNaN(an)) return -1;
        if (!isNaN(bn)) return 1;
        return a.localeCompare(b);
      });

      if (!this.uploadedFileId || !this.headers.length) {
        this.onSubmitError('پاسخ سرور نامعتبر بود (rows/id برگردانده نشد).');
        return;
      }

      this.submitStep = 'preview';
    },

    headerLabel(h) {
      return `ستون ${this.headers.indexOf(h) + 1}`;
    },
    typeForHeader(headerKey) {
      const idx = Number(headerKey);
      for (const [type, i] of Object.entries(this.selectedHeaders)) {
        if (i === idx) return type;
      }
      return null;
    },
    headerBg(type) {
      return (HEADER_COLOR_MAP[type] || {}).bg || 'bg-gray-50';
    },
    headerText(type) {
      return (HEADER_COLOR_MAP[type] || {}).text || 'text-gray-600';
    },
    formatCell(value) {
      if (value === null || value === undefined) return '';
      const s = String(value);
      return s.length > 18 ? `${s.slice(0, 18)}…` : s;
    },
    showHeaderPopup(h) {
      this.clickedHeader = h;
      this.popupVisible = true;
    },
    setHeaderType(type) {
      const headerIndex = Number(this.clickedHeader);
      if (this.selectedHeaders[type] === headerIndex) {
        this.$delete(this.selectedHeaders, type);
      } else {
        for (const key in this.selectedHeaders) {
          if (this.selectedHeaders[key] === headerIndex) this.$delete(this.selectedHeaders, key);
        }
        this.$set(this.selectedHeaders, type, headerIndex);
      }
      this.popupVisible = false;
    },
    cancelPreview() {
      this.submitStep = 'idle';
      this.submitError = '';
      this.uploadedFileId = null;
      this.rows = [];
      this.headers = [];
      this.selectedHeaders = {};
    },

    createExtractedReport() {
      if (!this.canCreateReport) return;
      this.submitStep = 'extracting';
      this.submitError = '';
      const path = this.packingType === 'domestic' ? 'file_handler/extractDomesticPacking' : 'file_handler/extractPacking';

      this.request({
        url: this.endpoint(path),
        method: 'post',
        data: { file_id: this.uploadedFileId, selected_headers: this.selectedHeaders },
        success: (result) => {
          this.reportResult = result;
          this.submitStep = 'done';
          this.submitError = '';
          // Deliberately NOT clearing items/draft here: the list must only ever
          // be emptied by the explicit "پاک‌سازی لیست" button + confirm, never
          // automatically — this lets the user review what was just submitted.
          // justSubmitted locks the list (no more scanning/editing/resubmitting)
          // until they clear it, so it can't be silently resubmitted as a duplicate.
          this.justSubmitted = true;
          this.persistDraft();
        },
        error: (err) => {
          const detail = extractBackendErrorDetail(err);
          const base = 'ساخت گزارش پکینگ با خطا مواجه شد.';
          this.onSubmitError(`${detail ? base + ' جزئیات: ' + detail : base} لیست و ستون‌بندی شما حفظ شده، دوباره تلاش کنید.`);
        },
      });
    },

    onSubmitError(message) {
      this.submitStep = 'error';
      this.submitError = message;
    },

    dismissSuccess() {
      // Closes the success confirmation only — the list stays intact and locked
      // (justSubmitted) until the user explicitly clears it above the list.
      this.reportResult = null;
      this.submitStep = 'idle';
    },
    // Same post-report navigation as the original desktop flow (both
    // ManagerEntrancePackageUpload.vue and ManagerDomesticEntrancePackageUpload.vue
    // use these exact identical paths regardless of packing type).
    goDetail(id) {
      this.$router.push(`/panel/entrancePackageItemDetail/${id}`);
    },
    goList() {
      this.$router.push('/panel/extractedEntrancePackageList');
    },

    // Inline login, so an unauthenticated visit to this PWA never leaves the
    // page for the separate desktop Login form (see the exclusion for
    // 'PackingEntry' in src/mixin/index.js's global auth-redirect guard).
    // Mirrors src/views/auth/Login.vue's login() exactly: same endpoint,
    // payload shape, and 2FA handling.
    focusLoginPassword() {
      if (this.$refs.loginPasswordInput) this.$refs.loginPasswordInput.focus();
    },
    submitLogin() {
      if (this.loggingIn) return;
      this.loginError = '';

      if (!this.loginUsername.trim() || !this.loginPassword) {
        this.loginError = 'نام کاربری و رمز عبور را وارد کنید.';
        return;
      }

      this.loggingIn = true;
      this.request({
        url: this.endpoint('login'),
        method: 'post',
        data: {
          username: this.loginUsername.trim(),
          password: this.loginPassword,
          code: this.twoFactorCode,
        },
        token: false,
        success: (data) => {
          this.loggingIn = false;
          if (data.need_two_factor_authentication) {
            this.needTwoFactor = true;
            return;
          }
          this.setToken(data.token);
          this.loginPassword = '';
          this.twoFactorCode = '';
          this.$nextTick(() => this.focusCodeInput());
        },
        error: (err) => {
          this.loggingIn = false;
          const detail = extractBackendErrorDetail(err);
          this.loginError = detail || 'نام کاربری یا رمز عبور اشتباه است.';
        },
      });
    },

    // The site's manifest.json is shared by the whole origin and points
    // start_url at "/" (the admin dashboard) — installing from any regular
    // page must keep opening the dashboard. This page swaps in its own
    // manifest (start_url: /packing-entry) only while mounted here, so
    // "Add to Home Screen" from THIS page installs an icon that opens
    // straight into scanning instead of the dashboard, without changing
    // what installing from anywhere else in the app does.
    swapToPackingManifest() {
      const link = document.querySelector('link[rel="manifest"]');
      if (!link) return;
      this._originalManifestHref = link.getAttribute('href');
      link.setAttribute('href', '/manifest-packing.json');
    },
    restoreOriginalManifest() {
      const link = document.querySelector('link[rel="manifest"]');
      if (link && this._originalManifestHref) {
        link.setAttribute('href', this._originalManifestHref);
      }
    },

    onBeforeInstallPrompt(e) {
      e.preventDefault();
      this.deferredInstallEvent = e;
      this.showInstallButton = true;
    },
    async installApp() {
      if (!this.deferredInstallEvent) return;
      this.deferredInstallEvent.prompt();
      try {
        await this.deferredInstallEvent.userChoice;
      } catch (e) {
        // noop
      }
      this.deferredInstallEvent = null;
      this.showInstallButton = false;
    },
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
